import React, { useCallback, useRef, useState } from "react"
import * as Icons from "lucide-react"
import { previewText } from "../utils/emailText"

function formatDate(dateStr) {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })
  if (diffDays === 0) return time
  if (diffDays === 1) return `Yesterday`
  if (diffDays < 7)
    return `${date.toLocaleDateString([], { weekday: "short" })} ${time}`
  if (date.getFullYear() === now.getFullYear())
    return date.toLocaleDateString([], { month: "short", day: "numeric" })
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function truncate(text, max) {
  if (!text) return ""
  return text.length > max ? text.slice(0, max) + "…" : text
}

// Avatar initials + a stable per-sender colour for the message-list avatars.
function avatarInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
  return (parts.map(w => w[0]).join("") || "?").toUpperCase()
}
const AVATAR_COLORS = [
  "#0f8a8a",
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#d97706",
  "#059669",
  "#dc2626",
  "#0891b2",
  "#4f46e5",
  "#c026d3",
]
function avatarColor(name) {
  const s = String(name || "")
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

// Outlook-style date grouping for the message list.
function dateGroup(dateStr) {
  if (!dateStr) return "Older"
  const d = new Date(dateStr)
  const now = new Date()
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  )
  const thatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.round((startOfToday - thatDay) / 86400000)
  if (diffDays <= 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return "Last week"
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth())
    return "This month"
  return d.toLocaleDateString([], { month: "long", year: "numeric" })
}

export default function EmailList({
  messages,
  loading,
  loadingMore,
  activeMessage,
  selectedMessages,
  onSelect,
  onSelectAll,
  onMessageClick,
  onToggleStar,
  onToggleFlag,
  onContextMenu,
  onLoadMore,
  activeFolder,
  folderSyncing,
  density = "normal",
  showPreview = true,
  showConversations = false,
  sortBy = "date",
  sortDir = "desc",
  page = 1,
  pageSize = 50,
  total = 0,
  onNextPage,
  onPrevPage,
}) {
  const scrollRef = useRef(null)
  const [filter, setFilter] = useState("all") // 'all' | 'unread'

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = (page - 1) * pageSize + messages.length
  const hasPrev = page > 1
  const hasNext = rangeEnd < total
  const goPrev = useCallback(() => {
    if (hasPrev && onPrevPage) onPrevPage()
  }, [hasPrev, onPrevPage])
  const goNext = useCallback(() => {
    if (hasNext && onNextPage) onNextPage()
  }, [hasNext, onNextPage])

  if ((loading || folderSyncing) && messages.length === 0) {
    return (
      <div className="em-list">
        <div className="em-list-header">
          <span className="em-list-loading">
            {folderSyncing ? "Syncing folder…" : "Loading…"}
          </span>
        </div>
        <div className="em-list-body">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="em-mail-item em-mail-item-skeleton">
              <div className="em-skeleton-content">
                <div className="em-skeleton em-skeleton-line w-50" />
                <div className="em-skeleton em-skeleton-line w-80" />
                <div className="em-skeleton em-skeleton-line w-60" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!loading && messages.length === 0) {
    return (
      <div className="em-list">
        <div className="em-list-empty">
          <Icons.Inbox size={40} strokeWidth={1} />
          <h3>No messages</h3>
          <p>Your {activeFolder || "inbox"} is empty</p>
        </div>
      </div>
    )
  }

  const baseSubject = s =>
    String(s || "")
      .replace(/^(re|fwd|fw):\s*/i, "")
      .trim()
  const timeOf = m => new Date(m.received_at || m.sent_at || 0).getTime()
  const sortMessages = arr => {
    const copy = [...arr]
    if (showConversations) {
      copy.sort((a, b) => {
        const sa = baseSubject(a.subject).toLowerCase()
        const sb = baseSubject(b.subject).toLowerCase()
        if (sa !== sb) return sa < sb ? -1 : 1
        return timeOf(b) - timeOf(a)
      })
      return copy
    }
    const flip = sortDir === "asc" ? -1 : 1
    copy.sort((a, b) => {
      if (sortBy === "from") {
        const fa = (
          a.from_display ||
          a.from_name ||
          a.from_address ||
          ""
        ).toLowerCase()
        const fb = (
          b.from_display ||
          b.from_name ||
          b.from_address ||
          ""
        ).toLowerCase()
        return (fa < fb ? -1 : fa > fb ? 1 : 0) * (sortDir === "asc" ? 1 : -1)
      }
      if (sortBy === "flag")
        return (
          ((b.is_flagged ? 1 : 0) - (a.is_flagged ? 1 : 0)) *
          (sortDir === "asc" ? -1 : 1)
        )
      return (timeOf(b) - timeOf(a)) * flip
    })
    return copy
  }
  const displayMessages = sortMessages(
    filter === "unread" ? messages.filter(m => m.is_read === false) : messages
  )
  const sortLabel = showConversations
    ? "By Conversation"
    : sortBy === "from"
    ? "By Sender"
    : sortBy === "flag"
    ? "By Flag"
    : "By Date"
  let lastGroup = null

  return (
    <div
      className={`em-list ${density === "compact" ? "em-list-compact" : ""} ${
        showPreview ? "" : "em-list-nopreview"
      }`}
    >
      <div className="em-list-header">
        <div className="em-list-filters">
          <button
            className={`em-list-filter ${
              filter === "all" ? "em-list-filter-active" : ""
            }`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={`em-list-filter ${
              filter === "unread" ? "em-list-filter-active" : ""
            }`}
            onClick={() => setFilter("unread")}
          >
            Unread
          </button>
        </div>
        <div
          className="em-list-sort"
          title={`Sorted ${sortDir === "asc" ? "ascending" : "descending"}`}
        >
          <span>{sortLabel}</span>
          <Icons.ArrowDownUp size={13} />
        </div>
      </div>

      {folderSyncing && (
        <div className="em-list-syncbar">
          <span className="em-spinner" /> Syncing this folder…
        </div>
      )}

      <div className="em-list-body" ref={scrollRef}>
        {displayMessages.map(msg => {
          const id = msg.id || msg.message_id
          const isActive =
            activeMessage &&
            (activeMessage.id === id || activeMessage.message_id === id)
          const sender =
            msg.from_display || msg.from_name || msg.from_address || "Unknown"
          const preview =
            previewText(msg.body_text) || previewText(msg.body_html)
          const g = showConversations
            ? baseSubject(msg.subject) || "(no subject)"
            : dateGroup(msg.received_at || msg.sent_at)
          const showGroup = g !== lastGroup
          lastGroup = g
          return (
            <React.Fragment key={id}>
              {showGroup && <div className="em-mail-group">{g}</div>}
              <div
                className={`em-mail-item ${
                  msg.is_read === false ? "em-mail-unread" : ""
                } ${isActive ? "em-mail-active" : ""}`}
                onClick={() => onMessageClick(msg)}
                onContextMenu={e => onContextMenu(e, msg)}
              >
                <span
                  className="em-mail-avatar"
                  style={{ background: avatarColor(sender) }}
                >
                  {avatarInitials(sender)}
                </span>
                <div className="em-mail-body">
                  <div className="em-mail-row1">
                    <span
                      className="em-mail-sender"
                      title={msg.from_address || sender}
                    >
                      {sender}
                    </span>
                    <span className="em-mail-date">
                      {formatDate(msg.received_at || msg.sent_at)}
                    </span>
                  </div>
                  <div className="em-mail-subject-line">
                    {msg.subject || "(no subject)"}
                  </div>
                  <div className="em-mail-preview-line">
                    {truncate(preview, 120) || "No preview available"}
                  </div>
                </div>
                <div className="em-mail-side">
                  {msg.has_attachments && (
                    <Icons.Paperclip
                      size={12}
                      className="em-mail-attach-icon"
                    />
                  )}
                  {msg.is_flagged && (
                    <Icons.Flag size={11} fill="#ef4444" color="#ef4444" />
                  )}
                  <span
                    className="em-mail-star"
                    onClick={e => {
                      e.stopPropagation()
                      onToggleStar(msg)
                    }}
                  >
                    <Icons.Star
                      size={14}
                      fill={msg.is_starred ? "#f59e0b" : "none"}
                      color={msg.is_starred ? "#f59e0b" : "#c8c8c8"}
                    />
                  </span>
                </div>
              </div>
            </React.Fragment>
          )
        })}
        {filter === "unread" && displayMessages.length === 0 && (
          <div className="em-list-nounread">No unread messages.</div>
        )}
        {loadingMore && (
          <div className="em-loading-more">
            <div className="em-spinner" /> Loading more…
          </div>
        )}
      </div>

      <div className="em-list-pager">
        <span className="em-list-pager-range">
          {total > 0 ? `${rangeStart}–${rangeEnd} of ${total}` : "0"}
        </span>
        <button
          className="em-page-btn"
          onClick={goPrev}
          disabled={!hasPrev || loading}
          title="Previous"
        >
          <Icons.ChevronLeft size={15} />
        </button>
        <button
          className="em-page-btn"
          onClick={goNext}
          disabled={!hasNext || loading}
          title="Next"
        >
          <Icons.ChevronRight size={15} />
        </button>
      </div>
    </div>
  )
}
